import { AuthCheck } from '@zg-rentals/particles-js-auth';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { AppContext } from '../../contexts/AppContextProvider';
import brandUtils from '../../utils/brandUtils';
import Custom500 from '../../pages/500';
import { useBrandedUserData } from '../../hooks/useBrandedUserData';

type Props = WithChildren<{
  requireStrongAuthentication: boolean;
}>;

export default function AuthCheckWrapper({ requireStrongAuthentication, children }: Props) {
  const router = useRouter();
  const { asPath, basePath } = router;
  const { brand, paths } = useContext(AppContext);
  const isHotPadsBrand = brandUtils.isHotpads(brand);

  const { user, isLoading, error } = useBrandedUserData();

  if (error) {
    return <Custom500 brand={brand} paths={paths} />;
  }

  // TODO: Replace return null in this check with pre-loading skeleton
  if (isLoading || !user) {
    return null;
  }

  const {
    hasStrongAuthentication,
    impersonatorEmail,
    impersonatorUserToken,
    isTwoFactorAuthEnabled,
    isTwoFactorAuthenticated,
    loggedIn,
    userToken,
  } = user || {};

  // Redirects soft login HotPads users to set a password in a non-dev environment if they try to access inbox without it
  if (
    process.env.NODE_ENV !== 'development' &&
    typeof window !== 'undefined' &&
    isHotPadsBrand &&
    loggedIn &&
    !userToken
  ) {
    const hotpadsSetPasswordPage = `${window.location.origin}/user/update-password?redirect=/renter-hub/inbox`;
    window.location.href = hotpadsSetPasswordPage;
  }

  const handleAuthCheckRedirect = (redirectUrl: string) => redirectUrl;

  const routing = {
    location: {
      pathname: `${basePath}${asPath}`,
      search: '',
    },
  };

  return (
    <AuthCheck
      brand={brand}
      hasStrongAuthentication={hasStrongAuthentication}
      impersonatorEmail={impersonatorEmail}
      impersonatorUserToken={impersonatorUserToken}
      isReadyToClientSideRedirect={true}
      isTwoFactorAuthEnabled={isTwoFactorAuthEnabled}
      isTwoFactorAuthenticated={isTwoFactorAuthenticated}
      loggedIn={loggedIn}
      onRedirectCallback={handleAuthCheckRedirect}
      requireStrongAuthentication={requireStrongAuthentication}
      routing={routing}
      userToken={userToken}
    >
      {children}
    </AuthCheck>
  );
}
